import React, { createContext, useContext, useMemo, ReactNode, useState, useCallback } from 'react';
import { getFunnelsStepNames } from '../helpers';
import { getRecordTypeNames } from '../selected-object/utils';
import { RecordTypesLabelsByNameAndObjectName } from '../types';
import { EntityClicked } from './types';
import { useDispatch } from 'react-redux';
import {
  setActivePageTab,
  DEFAULT_OBJECTS_TAB_ID,
} from '../../../reducers/documentation/documentationPageReducer';

/**
 * This context holds only HIGH LEVEL information and functions
 * that should be delivered to Documentation but are part of other apps
 * eg. canvasFunnelMap
 */

interface DocumentationContextValue {
  focusOnCanvasElements?: (
    elementIds: string[],
    options?: { maxZoom?: number },
    fitAroundFirstStepOnly?: boolean,
  ) => void;

  objectNamesUsedInCanvas: string[];
  stepNamesUsedInCanvasByObjectName: { [objectName: string]: string[] };
  recordTypesUsedInCanvasByObjectName: RecordTypesLabelsByNameAndObjectName;
  entityClicked: EntityClicked | null;
  setEntityClicked: (entity: EntityClicked | null) => void;
  canvasFunnelMap?: FunnelMap;
}

interface DocumentationContextProviderProps {
  children: ReactNode;
  focusOnCanvasElements?: (
    elementIds: string[],
    options?: { maxZoom?: number },
    fitAroundFirstStepOnly?: boolean,
  ) => void;
  canvasFunnelMap?: FunnelMap;
  crmOrgId: string;
}

const DocumentationContext = createContext<DocumentationContextValue | undefined>(undefined);

export const DocumentationContextProvider: React.FC<DocumentationContextProviderProps> = ({
  children,
  focusOnCanvasElements,
  canvasFunnelMap,
  crmOrgId,
}) => {
  const dispatch = useDispatch();
  const [entityClicked, setEntityClicked] = useState<EntityClicked | null>(null);
  const funnelsData = canvasFunnelMap?.funnelsData;
  const recordTypesData = canvasFunnelMap?.recordTypesData;

  const stepNamesUsedInCanvasByObjectName = useMemo(() => {
    return getFunnelsStepNames({
      funnelsData,
      recordTypesData,
      crmOrgId,
    });
  }, [funnelsData, recordTypesData, crmOrgId]);

  const recordTypesUsedInCanvasByObjectName = useMemo(() => {
    return getRecordTypeNames({
      funnelsData: funnelsData,
      recordTypesData: recordTypesData,
      crmOrgId,
    });
  }, [funnelsData, recordTypesData, crmOrgId]);

  const objectNamesUsedInCanvas = Object.keys(recordTypesUsedInCanvasByObjectName);

  const _setEntityClicked = useCallback(
    (entity: EntityClicked | null) => {
      entity && dispatch(setActivePageTab({ tabId: DEFAULT_OBJECTS_TAB_ID }));
      setEntityClicked(entity);
    },
    [dispatch],
  );

  const value: DocumentationContextValue = useMemo(
    () => ({
      focusOnCanvasElements,
      objectNamesUsedInCanvas,
      stepNamesUsedInCanvasByObjectName,
      recordTypesUsedInCanvasByObjectName,
      setEntityClicked: _setEntityClicked,
      entityClicked,
      canvasFunnelMap,
    }),
    [
      focusOnCanvasElements,
      objectNamesUsedInCanvas,
      stepNamesUsedInCanvasByObjectName,
      recordTypesUsedInCanvasByObjectName,
      _setEntityClicked,
      entityClicked,
      canvasFunnelMap,
    ],
  );

  return <DocumentationContext.Provider value={value}>{children}</DocumentationContext.Provider>;
};

export const useDocumentationContext = () => {
  const context = useContext(DocumentationContext);
  if (context === undefined) {
    throw new Error('useDocumentationContext must be used within a DocumentationContextProvider');
  }
  return context;
};
